<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

// GK-HUB: vue-chartjs Bar + Line for dual stacked evolution chart (Option B)
import { Bar, Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Filler,
} from 'chart.js';

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Filler
);
// GK-HUB: end chart.js imports

import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import IspMetricCard from './components/IspMetricCard.vue';
import IspBarChart from './components/IspBarChart.vue';
import IspDataTable from './components/IspDataTable.vue';
import IspHeatmap from './components/IspHeatmap.vue';

import { useIspReport } from './composables/useIspReport';
import { exportCsv } from './composables/useIspExport';
// GK-HUB: Import role helper for team filter visibility
import { getUserRole } from 'dashboard/helper/permissionsHelper';
import './styles/isp-filters.css';

const { t } = useI18n();
const store = useStore();

// GK-HUB: Removed showDeptFilter/departments/selectedDeptId — using team filter instead
const {
  data: queueMetrics,
  retry,
  customDateRange,
  selectedDateRange,
  onDateRangeChange,
  onFilterChange,
  filters,
} = useIspReport({
  getter: 'ispReports/getQueueMetrics',
  action: 'ispReports/fetchQueueMetrics',
  filters: { teamId: '' },
  initActions: ['teams/get'],
});

// GK-HUB: Teams list + showTeamFilter (admin/manager only)
const teams = computed(() => store.getters['teams/getTeams'] || []);
const currentUser = computed(() => store.getters.getCurrentUser);
const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const showTeamFilter = computed(() =>
  ['administrator', 'manager'].includes(
    getUserRole(currentUser.value, currentAccountId.value)
  )
);
// Queue teams for the filter (only teams with queue enabled)
const queueTeams = computed(() =>
  teams.value.filter(team => team.queue_enabled)
);
// GK-HUB: end team filter

// Helpers
const formatDuration = seconds => {
  if (!seconds || seconds <= 0) return '0s';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.round(seconds % 60);
  if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  return `${s}s`;
};

// Summary data
const summary = computed(() => queueMetrics.value?.data?.summary || {});
const evolucao = computed(() => queueMetrics.value?.data?.evolucao || []);
const capacidade = computed(() => queueMetrics.value?.data?.capacidade || []);
const porTime = computed(() => queueMetrics.value?.data?.por_time || []);
const porAgente = computed(() => queueMetrics.value?.data?.por_agente || []);
const heatmapData = computed(() => queueMetrics.value?.data?.heatmap || []);
const hasHeatmapData = computed(() =>
  heatmapData.value.some(item => item.count > 0)
);

// KPI trend helpers
const trendTotal = computed(() => {
  const val = summary.value.trend_total;
  if (!val) return '';
  const sign = val > 0 ? '+' : '';
  return `${sign}${val}% ${t('ISP_REPORTS.QUEUE_METRICS.TREND_VS')}`;
});
const trendTotalDir = computed(() => {
  const val = summary.value.trend_total;
  if (!val) return 'neutral';
  return val > 0 ? 'up' : 'down';
});

const trendWait = computed(() => {
  const val = summary.value.trend_wait;
  if (!val) return '';
  const sign = val > 0 ? '+' : '';
  return `${sign}${formatDuration(Math.abs(val))} ${t('ISP_REPORTS.QUEUE_METRICS.TREND_VS')}`;
});
const trendWaitDir = computed(() => {
  const val = summary.value.trend_wait;
  if (!val) return 'neutral';
  // Lower wait = better → negative diff = improvement = up (green)
  return val < 0 ? 'up' : 'down';
});

const trendAbandono = computed(() => {
  const val = summary.value.trend_abandono;
  if (!val) return '';
  const sign = val > 0 ? '+' : '';
  return `${sign}${val}% ${t('ISP_REPORTS.QUEUE_METRICS.TREND_VS')}`;
});
const trendAbandonoDir = computed(() => {
  const val = summary.value.trend_abandono;
  if (!val) return 'neutral';
  // Lower abandonment = better → negative diff = improvement = up (green)
  return val < 0 ? 'up' : 'down';
});

// GK-HUB: Dual stacked charts — Option B (bars + area)
const evolucaoLabels = computed(() => {
  return evolucao.value.map(item => {
    const d = new Date(item.date + 'T00:00:00');
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  });
});

const gridColor = 'rgba(148, 163, 184, 0.06)';
const tickFont = { family: 'Inter, sans-serif', size: 10 };
const tickColor = '#64748b';
const tooltipBase = {
  backgroundColor: 'rgba(15, 23, 42, 0.95)',
  titleFont: { family: 'Inter, sans-serif', size: 11, weight: '600' },
  bodyFont: { family: 'Inter, sans-serif', size: 12 },
  titleColor: '#94a3b8',
  bodyColor: '#e2e8f0',
  borderColor: 'rgba(255,255,255,0.08)',
  borderWidth: 1,
  padding: { top: 8, bottom: 8, left: 12, right: 12 },
  cornerRadius: 8,
  usePointStyle: true,
};
const legendBase = {
  position: 'top',
  align: 'end',
  labels: {
    usePointStyle: true,
    pointStyle: 'circle',
    boxWidth: 6,
    boxHeight: 6,
    padding: 14,
    font: { family: 'Inter, sans-serif', size: 11, weight: '500' },
    color: '#94a3b8',
  },
};

// Top chart: grouped bars (conversas + abandonos)
const evolucaoBarData = computed(() => ({
  labels: evolucaoLabels.value,
  datasets: [
    {
      label: t('ISP_REPORTS.QUEUE_METRICS.SERIE_TOTAL'),
      data: evolucao.value.map(i => i.total),
      backgroundColor: 'rgba(93, 173, 226, 0.7)',
      borderColor: '#5DADE2',
      borderWidth: 1,
      borderRadius: 3,
      barPercentage: 0.7,
      categoryPercentage: 0.8,
    },
    {
      label: t('ISP_REPORTS.QUEUE_METRICS.SERIE_ABANDONOS'),
      data: evolucao.value.map(i => i.abandonos),
      backgroundColor: 'rgba(239, 68, 68, 0.7)',
      borderColor: '#ef4444',
      borderWidth: 1,
      borderRadius: 3,
      barPercentage: 0.7,
      categoryPercentage: 0.8,
    },
  ],
}));

const evolucaoBarOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: legendBase,
    tooltip: tooltipBase,
  },
  scales: {
    x: {
      grid: { color: gridColor, drawBorder: false },
      ticks: { font: tickFont, color: tickColor, padding: 6, maxRotation: 0 },
      border: { display: false },
    },
    y: {
      beginAtZero: true,
      grid: { color: gridColor, drawBorder: false },
      ticks: { font: tickFont, color: tickColor, padding: 8, precision: 0 },
      border: { display: false },
    },
  },
  animation: { duration: 600, easing: 'easeOutQuart' },
}));

// Bottom chart: area line (tempo na fila em minutos)
const gradientPlugin = {
  id: 'ispGradientFill',
  beforeDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, i) => {
      if (dataset.fill && dataset.customFillColor) {
        const meta = chart.getDatasetMeta(i);
        if (!meta.dataset) return;
        const yAxis = chart.scales.y;
        const gradient = ctx.createLinearGradient(
          0,
          yAxis.top,
          0,
          yAxis.bottom
        );
        gradient.addColorStop(0, dataset.customFillColor);
        gradient.addColorStop(1, dataset.customFillColorEnd || 'rgba(0,0,0,0)');
        dataset.backgroundColor = gradient;
      }
    });
  },
};

const evolucaoAreaData = computed(() => ({
  labels: evolucaoLabels.value,
  datasets: [
    {
      label: t('ISP_REPORTS.QUEUE_METRICS.SERIE_TEMPO_FILA'),
      data: evolucao.value.map(i =>
        parseFloat((i.tempo_fila_seconds / 60).toFixed(1))
      ),
      borderColor: '#f59e0b',
      backgroundColor: 'rgba(245, 158, 11, 0.12)',
      pointBackgroundColor: '#f59e0b',
      pointBorderColor: '#1e1e2e',
      pointBorderWidth: 2,
      pointRadius: 3,
      pointHoverRadius: 5,
      borderWidth: 2,
      tension: 0.4,
      fill: true,
      customFillColor: 'rgba(245, 158, 11, 0.12)',
      customFillColorEnd: 'rgba(245, 158, 11, 0.01)',
    },
  ],
}));

const evolucaoAreaOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: legendBase,
    tooltip: {
      ...tooltipBase,
      callbacks: {
        label: ctx => ` ${ctx.dataset.label}: ${ctx.parsed.y} min`,
      },
    },
  },
  scales: {
    x: {
      grid: { color: gridColor, drawBorder: false },
      ticks: { font: tickFont, color: tickColor, padding: 6, maxRotation: 0 },
      border: { display: false },
    },
    y: {
      beginAtZero: true,
      grid: { color: gridColor, drawBorder: false },
      ticks: {
        font: tickFont,
        color: tickColor,
        padding: 8,
        callback: v => v + ' min',
      },
      border: { display: false },
    },
  },
  animation: { duration: 600, easing: 'easeOutQuart' },
}));
// GK-HUB: end dual stacked charts

// Capacity bar items
const capacityItems = computed(() => {
  return capacidade.value.map(team => {
    let color = 'teal';
    if (team.occupancy > 85) color = 'warning';
    else if (team.occupancy > 60) color = 'blue';

    return {
      label: team.team_name,
      value: team.occupancy,
      displayValue: `${team.occupancy}%`,
      percentage: team.occupancy,
      color,
      subtitle: `${team.total} conv · ${team.agents_count} ag · max ${team.max_per_agent}/ag`,
    };
  });
});

// Bar chart items (conversations by team)
const getBarColor = idx => {
  if (idx < 2) return 'teal';
  if (idx < 4) return 'blue';
  return 'warning';
};

const barChartItems = computed(() => {
  const data = porTime.value;
  if (!data.length) return [];
  const sum = data.reduce((acc, item) => acc + (item.total || 0), 0) || 1;

  return data.map((team, i) => ({
    label: team.team_name,
    value: team.total,
    displayValue: String(team.total),
    percentage: Math.round((team.total / sum) * 100),
    color: getBarColor(i),
  }));
});

// Agent performance table (refactored columns with widths)
const tableColumns = [
  {
    key: 'agent',
    label: t('ISP_REPORTS.QUEUE_METRICS.COL_AGENTE'),
    width: '28%',
  },
  {
    key: 'atendimentos',
    label: t('ISP_REPORTS.QUEUE_METRICS.COL_ATENDIMENTOS'),
    align: 'right',
    width: '18%',
  },
  {
    key: 'tempo_fila',
    label: t('ISP_REPORTS.QUEUE_METRICS.COL_TEMPO_FILA'),
    align: 'right',
    type: 'mono',
    width: '18%',
  },
  {
    key: 'maior_espera',
    label: t('ISP_REPORTS.QUEUE_METRICS.COL_MAIOR_ESPERA'),
    align: 'right',
    type: 'mono',
    width: '18%',
  },
  {
    key: 'percentual',
    label: t('ISP_REPORTS.QUEUE_METRICS.COL_CARGA'),
    align: 'right',
    type: 'badge',
    width: '18%',
  },
];

const tableRows = computed(() =>
  porAgente.value.map(a => ({
    agent: a.agent_name,
    atendimentos: a.atendimentos,
    tempo_fila: formatDuration(a.tempo_fila_seconds),
    maior_espera: formatDuration(a.maior_espera_seconds),
    percentual: `${a.percentual}%`,
  }))
);

const onExportCsv = () => {
  const agents = porAgente.value;
  exportCsv(
    agents,
    'Agente,Atendimentos,Tempo Fila (s),Maior Espera (s),% Carga\n',
    r =>
      `"${r.agent_name || ''}",${r.atendimentos || 0},${r.tempo_fila_seconds || 0},${r.maior_espera_seconds || 0},${r.percentual || 0}`,
    'metricas-fila.csv'
  );
};
</script>

<template>
  <div class="p-6">
    <!-- Header -->
    <div class="mb-4">
      <h1 class="text-xl font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.QUEUE_METRICS.HEADER') }}
      </h1>
      <p class="text-[13px] text-n-slate-10 mt-0.5">
        {{ t('ISP_REPORTS.QUEUE_METRICS.SUBTITLE') }}
      </p>
    </div>

    <!-- Filters -->
    <div class="flex items-center gap-3 mb-6 flex-wrap">
      <WootDatePicker
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />
      <!-- GK-HUB: Team filter (replaces department) -->
      <div v-if="showTeamFilter" class="isp-select-wrap">
        <select
          :value="filters.teamId.value"
          class="isp-select"
          @change="onFilterChange('teamId', $event.target.value)"
        >
          <option value="">
            {{ t('ISP_REPORTS.QUEUE_METRICS.FILTER_ALL_TEAMS') }}
          </option>
          <option v-for="team in queueTeams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
        <span class="isp-select-arrow i-lucide-chevron-down" />
      </div>
      <!-- GK-HUB: end team filter -->
      <button class="btn-export ml-auto" @click="onExportCsv">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        {{ t('ISP_REPORTS.FILTERS.EXPORT_CSV') }}
      </button>
    </div>

    <!-- Loading -->
    <woot-loading-state
      v-if="queueMetrics.isFetching && !queueMetrics.data"
      :message="t('ISP_REPORTS.LOADING')"
      class="h-64"
    />

    <!-- Error -->
    <div
      v-else-if="queueMetrics.error && !queueMetrics.data"
      class="flex flex-col items-center justify-center h-64 gap-3"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.ERROR_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10">
        {{ t('ISP_REPORTS.ERROR_MESSAGE') }}
      </p>
      <button
        class="bg-n-brand text-white rounded-md text-sm font-medium px-4 py-2"
        @click="retry"
      >
        {{ t('ISP_REPORTS.RETRY') }}
      </button>
    </div>

    <template v-if="queueMetrics.data">
      <!-- Metric Cards (5 KPIs) -->
      <div
        class="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4 mb-6"
      >
        <IspMetricCard
          :label="t('ISP_REPORTS.QUEUE_METRICS.TOTAL_FILA')"
          :value="summary.total_atendimentos || 0"
          :trend="trendTotal"
          :trend-direction="trendTotalDir"
          :loading="queueMetrics.isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.QUEUE_METRICS.TEMPO_MEDIO_FILA')"
          :value="formatDuration(summary.tempo_medio_fila || 0)"
          :trend="trendWait"
          :trend-direction="trendWaitDir"
          :loading="queueMetrics.isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.QUEUE_METRICS.MAIOR_ESPERA')"
          :value="formatDuration(summary.maior_espera || 0)"
          :loading="queueMetrics.isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.QUEUE_METRICS.TAXA_ABANDONO')"
          :value="`${summary.taxa_abandono || 0}%`"
          :trend="trendAbandono"
          :trend-direction="trendAbandonoDir"
          :loading="queueMetrics.isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.QUEUE_METRICS.DISTRIBUICAO')"
          :value="`${summary.distribuicao || 0}%`"
          :loading="queueMetrics.isFetching"
        />
      </div>

      <!-- Capacity vs Demand -->
      <div class="mb-6">
        <IspBarChart
          v-if="capacityItems.length"
          :title="t('ISP_REPORTS.QUEUE_METRICS.CAPACIDADE_TITULO')"
          :subtitle="t('ISP_REPORTS.QUEUE_METRICS.CAPACIDADE_SUBTITLE')"
          :items="capacityItems"
        />
      </div>

      <!-- GK-HUB: Dual stacked charts — bars (volume) + area (tempo) -->
      <div v-if="evolucao.length > 1" class="flex flex-col mb-6">
        <div class="text-[13px] font-semibold text-n-slate-12 mb-4">
          {{ t('ISP_REPORTS.QUEUE_METRICS.EVOLUCAO_TITULO') }}
        </div>
        <div class="bg-n-solid-2 border border-n-weak rounded-[10px] p-5">
          <div class="text-[11px] text-n-slate-9 font-medium mb-2">
            {{ t('ISP_REPORTS.QUEUE_METRICS.EVOLUCAO_VOLUME_LABEL') }}
          </div>
          <div class="h-[180px]">
            <Bar :data="evolucaoBarData" :options="evolucaoBarOptions" />
          </div>
          <div class="h-5" />
          <div class="text-[11px] text-n-slate-9 font-medium mb-2">
            {{ t('ISP_REPORTS.QUEUE_METRICS.EVOLUCAO_TEMPO_LABEL') }}
          </div>
          <div class="h-[180px]">
            <Line
              :data="evolucaoAreaData"
              :options="evolucaoAreaOptions"
              :plugins="[gradientPlugin]"
            />
          </div>
        </div>
      </div>
      <!-- GK-HUB: end dual stacked charts -->

      <!-- Heatmap: Queue volume by day × hour -->
      <IspHeatmap
        v-if="hasHeatmapData"
        :title="t('ISP_REPORTS.QUEUE_METRICS.HEATMAP_TITULO')"
        :subtitle="t('ISP_REPORTS.QUEUE_METRICS.HEATMAP_SUBTITLE')"
        :data="heatmapData"
        :tooltip-unit="t('ISP_REPORTS.QUEUE_METRICS.HEATMAP_UNIT')"
        :legend-less="t('ISP_REPORTS.QUEUE_METRICS.HEATMAP_LESS')"
        :legend-more="t('ISP_REPORTS.QUEUE_METRICS.HEATMAP_MORE')"
        show-avg-wait
        show-count
        :avg-wait-label="t('ISP_REPORTS.QUEUE_METRICS.HEATMAP_AVG_WAIT')"
      />

      <!-- Bar Chart: Conversations by Team -->
      <div class="mb-6">
        <IspBarChart
          v-if="barChartItems.length"
          :title="t('ISP_REPORTS.QUEUE_METRICS.POR_TIME_TITULO')"
          :items="barChartItems"
        />
      </div>

      <!-- Table: Agent Performance -->
      <IspDataTable
        v-if="tableRows.length"
        :title="t('ISP_REPORTS.QUEUE_METRICS.POR_AGENTE_TITULO')"
        :columns="tableColumns"
        :rows="tableRows"
      />
    </template>

    <!-- Empty State -->
    <div
      v-if="
        !queueMetrics.isFetching && !queueMetrics.error && !queueMetrics.data
      "
      class="flex flex-col items-center justify-center h-64 gap-2"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.QUEUE_METRICS.EMPTY_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10">
        {{ t('ISP_REPORTS.QUEUE_METRICS.EMPTY_MESSAGE') }}
      </p>
    </div>
  </div>
</template>

<style scoped>
/* ── Filter select wrapper ───────────────────────────────────────────── */
.isp-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.isp-select-arrow {
  position: absolute;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  pointer-events: none;
  color: #9ca3af;
  flex-shrink: 0;
}

/* ── Filter select: base (light mode) ────────────────────────────────── */
.isp-select {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  width: 100%;
  height: auto;
  margin: 0;
  font-size: 12px;
  font-family: inherit;
  line-height: 1.5;
  padding: 6px 30px 6px 10px;
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  transition:
    border-color 150ms ease,
    background-color 150ms ease;
  background: none;
  background-color: #f8f9fb;
  color: #4b5563;
  border: 1px solid #e0e2e6;
}
.isp-select:hover {
  border-color: #cbd0d6;
}
.isp-select:focus {
  border-color: #2e86c1;
}

/* ── Export button: light mode ───────────────────────────────────────── */
.btn-export {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1.5px solid #d1d5db;
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease;
  color: #6b7280;
}
.btn-export:hover {
  border-color: #9ca3af;
  color: #374151;
}

/* ── DatePicker z-index fix ──────────────────────────────────────────── */
:deep(.date-picker) {
  z-index: 20;
}
</style>

<!-- Dark mode overrides (unscoped for reliable body.dark targeting) -->
<style>
/* ── Filter select: dark mode ────────────────────────────────────────── */
body.dark .isp-select {
  background-color: transparent;
  color: rgba(255, 255, 255, 0.72);
  border-color: rgba(255, 255, 255, 0.08);
}
body.dark .isp-select:hover {
  border-color: rgba(255, 255, 255, 0.14);
  background-color: rgba(255, 255, 255, 0.03);
}
body.dark .isp-select-arrow {
  color: #64748b;
}
/* ── Export button: dark mode ────────────────────────────────────────── */
body.dark .btn-export {
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
body.dark .btn-export:hover {
  border-color: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.85);
}
</style>
